// Imports
import { getBaseUrl } from "./baseEndpoint";
import {
  Category,
  SubCategory,
  Package,
  Language,
  ContentType,
  PackageContent,
  CreateStepData,
  LinksResponse,
  LinkDataPost,
  License,
  PostLicense,
  PatchLicense,
} from "../../../Types/types";
import axiosInstance from "../../../axiosInstance";
import { formatFileSize } from "../../Manual_detail/formatFileSize";
import { SHARED_TOKEN_HEADER } from "../../../utils/sharedAccess";

// Utility Functions
const getToken = () => localStorage.getItem("token");

const getHeaders = () => ({
  headers: {
    Accept: "application/json",
    Authorization: `Bearer ${getToken()}`,
  },
});

const getSharedHeaders = (token: string) => ({
  headers: {
    Accept: "application/json",
    [SHARED_TOKEN_HEADER]: token,
  },
});

// Category-related Functions
export const fetchCategories = async (
  language: string
): Promise<Category[]> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/category?locale=${language}`,
    getHeaders()
  );
  return response.data.categories;
};

export const fetchCategoryById = async (
  id: number,
  locale: string
): Promise<Category> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/category/${id}?locale=${locale}`,
    getHeaders()
  );
  return response.data;
};

export const postCategory = async (categoryData: {
  name: Record<Language, string>;
}): Promise<Category> => {
  const response = await axiosInstance.post(
    `${getBaseUrl()}/category`,
    categoryData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const patchCategory = async (
  id: number,
  categoryData: { name: Record<Language, string> }
): Promise<Category> => {
  const response = await axiosInstance.patch(
    `${getBaseUrl()}/category/${id}`,
    categoryData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const deleteCategory = async (id: number): Promise<void> => {
  await axiosInstance.delete(`${getBaseUrl()}/category/${id}`, getHeaders());
};

export const uploadCategoryThumbnail = async (
  id: number,
  thumbnail: File
): Promise<void> => {
  const formData = new FormData();
  formData.append("thumbnail", thumbnail);

  await axiosInstance.post(
    `${getBaseUrl()}/category/${id}/thumbnail`,
    formData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

// SubCategory-related Functions

export const fetchSubCategories = async (
  language: string
): Promise<SubCategory[]> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/subcategory?locale=${language}`,
    getHeaders()
  );
  return response.data.subcategories;
};

export const fetchSubCategoriesForList = async (
  language: string,
  categoryId: number
): Promise<SubCategory[]> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/subcategory?locale=${language}&categoryId=${categoryId}`,
    getHeaders()
  );
  return response.data.subcategories;
};

export const fetchSubCategoryById = async (
  id: number,
  locale: string
): Promise<SubCategory> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/subcategory/${id}?locale=${locale}`,
    getHeaders()
  );
  return response.data;
};

export const postSubCategory = async (subCategoryData: {
  categoryId: number;
  name: Record<Language, string>;
}): Promise<SubCategory> => {
  const response = await axiosInstance.post(
    `${getBaseUrl()}/subcategory`,
    subCategoryData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const patchSubCategory = async (
  id: number,
  subCategoryData: { categoryId: number; name: Record<Language, string> }
): Promise<SubCategory> => {
  const response = await axiosInstance.patch(
    `${getBaseUrl()}/subcategory/${id}`,
    subCategoryData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const deleteSubCategory = async (id: number): Promise<void> => {
  await axiosInstance.delete(`${getBaseUrl()}/subcategory/${id}`, getHeaders());
};

// Package-related Functions
export const fetchPackages = async (
  language: string,
  categoryIds?: string[],
  subCategoryIds?: string[],
  page: number = 0,
  count: number = 20,
  searchQuery: string = "",
  order: string = "ASC"
): Promise<{ packages: Package[]; count: number }> => {
  const params = new URLSearchParams({
    locale: language,
    page: page.toString(),
    count: count.toString(),
  });

  if (searchQuery) {
    params.append("search", searchQuery);
  }

  if (categoryIds && categoryIds.length > 0) {
    params.append("categoryId", categoryIds.join(","));
  }

  if (subCategoryIds && subCategoryIds.length > 0) {
    params.append("subCategoryId", subCategoryIds.join(","));
  }

  const baseUrl = `${getBaseUrl()}/package?${params.toString()}`;
  const finalUrl = `${baseUrl}&ordering=${order}`;

  const response = await axiosInstance.get(finalUrl, getHeaders());

  return {
    packages: response.data.packages,
    count: response.data.count,
  };
};

export const fetchPackageById = async (
  id: string,
  locale: string
): Promise<Package> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/package/${id}?locale=${locale}`,
    getHeaders()
  );
  return response.data;
};

export const fetchSharedPackageById = async (
  id: string,
  locale: string,
  token: string
): Promise<Package> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/shared/package/${id}?locale=${locale}`,
    getSharedHeaders(token)
  );
  return response.data;
};

export const fetchFullPackageById = async (
  id: string,
  language: string
): Promise<Package> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/package/${id}/full?locale=${language}`,
    getHeaders()
  );
  return response.data;
};

export const postPackage = async (packageData: {
  subCategoryId: number;
  label: Record<Language, string>;
  description: Record<Language, string>;
}): Promise<Package> => {
  const response = await axiosInstance.post(
    `${getBaseUrl()}/package`,
    packageData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const deletePackage = async (id: number): Promise<void> => {
  await axiosInstance.delete(`${getBaseUrl()}/package/${id}`, getHeaders());
};

export const patchPackage = async (
  id: string,
  packageData: {
    subCategoryId: number;
    label: Record<Language, string>;
    description: Record<Language, string>;
  }
) => {
  const response = await axiosInstance.patch(
    `${getBaseUrl()}/package/${id}`,
    packageData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

// Thumbnail-related Functions
export const fetchThumbnailById = async (id: string): Promise<string> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/package/${id}/thumbnail`,
    getHeaders()
  );
  const base64Image = response.data;
  const imageUrl = `data:image/png;base64,${base64Image}`;
  return imageUrl;
};

export const fetchStepThumbnailById = async (id: string): Promise<string> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/packageContent/${id}/thumbnail`,
    getHeaders()
  );
  let base64Image = response.data;

  const prefix = "data:image/png;base64,";
  if (base64Image.startsWith(prefix)) {
    base64Image = base64Image.replace(new RegExp(`^${prefix}`), "");
  }

  const imageUrl = `${prefix}${base64Image}`;
  return imageUrl;
};

export const fetchSharedStepThumbnailById = async (
  id: string,
  token: string
): Promise<string> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/shared/packageContent/${id}/thumbnail`,
    getSharedHeaders(token)
  );
  let base64Image = response.data;

  const prefix = "data:image/png;base64,";
  if (base64Image.startsWith(prefix)) {
    base64Image = base64Image.replace(new RegExp(`^${prefix}`), "");
  }

  return `${prefix}${base64Image}`;
};

export const uploadThumbnail = async (
  id: number,
  thumbnail: File
): Promise<void> => {
  const formData = new FormData();
  formData.append("thumbnail", thumbnail);

  await axiosInstance.post(
    `${getBaseUrl()}/package/${id}/thumbnail`,
    formData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const uploadContentThumbnail = async (
  id: number,
  thumbnail: File
): Promise<void> => {
  const formData = new FormData();
  formData.append("thumbnail", thumbnail);

  await axiosInstance.post(
    `${getBaseUrl()}/packageContent/${id}/thumbnail`,
    formData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const deletePackageThumbnail = async (id: number): Promise<void> => {
  await axiosInstance.delete(
    `${getBaseUrl()}/package/${id}/thumbnail`,
    getHeaders()
  );
};

export const deleteContentThumbnail = async (id: number): Promise<void> => {
  await axiosInstance.delete(
    `${getBaseUrl()}/packageContent/${id}/thumbnail`,
    getHeaders()
  );
};

// Content-related Functions

export const createStep = async (stepData: CreateStepData) => {
  const response = await axiosInstance.post(
    `${getBaseUrl()}/packageContent`,
    stepData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const uploadContent = async (
  id: string,
  primaryFile: File,
  secondaryFile?: File
): Promise<void> => {
  const formData = new FormData();
  formData.set("content", primaryFile);

  if (secondaryFile) {
    formData.set("contentIos", secondaryFile);
  }

  await axiosInstance.post(
    `${getBaseUrl()}/packageContent/${id}/content`,
    formData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const fetchContentTypes = async (
  language: string
): Promise<ContentType[]> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/contentType?locale=${language}`,
    getHeaders()
  );
  return response.data.contentTypes;
};

export const fetchContentById = async (
  id: string
): Promise<{ url: string; size: string }> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/packageContent/${id}/content`,
    {
      ...getHeaders(),
      responseType: "blob",
    }
  );

  const contentLength = response.headers["content-length"];
  const fileSize = contentLength
    ? formatFileSize(parseInt(contentLength, 10))
    : "Size not available";

  const fileUrl = URL.createObjectURL(new Blob([response.data]));

  return { url: fileUrl, size: fileSize };
};

export const fetchSharedContentById = async (
  id: string,
  token: string
): Promise<{ url: string; size: string }> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/shared/packageContent/${id}/content`,
    {
      ...getSharedHeaders(token),
      responseType: "blob",
    }
  );

  const contentLength = response.headers["content-length"];
  const fileSize = contentLength
    ? formatFileSize(parseInt(contentLength, 10))
    : "Size not available";

  const fileUrl = URL.createObjectURL(new Blob([response.data]));

  return { url: fileUrl, size: fileSize };
};

export const fetchStepById = async (id: string): Promise<PackageContent> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/packageContent/${id}`,
    getHeaders()
  );
  return response.data;
};

export const deletePackageContent = async (id: number): Promise<void> => {
  await axiosInstance.delete(
    `${getBaseUrl()}/packageContent/${id}`,
    getHeaders()
  );
};

export const patchPackageContent = async (
  id: number,
  contentData: PackageContent
): Promise<PackageContent> => {
  const response = await axiosInstance.patch(
    `${getBaseUrl()}/packageContent/${id}`,
    contentData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

// License-related Functions
export const fetchLicenses = async (): Promise<License[]> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/license`,
    getHeaders()
  );
  return response.data.licenses;
};

export const fetchLicenseById = async (id: string): Promise<License> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/license/${id}`,
    getHeaders()
  );
  return response.data;
};

export const postLicense = async (licenseData: {
  name: string;
  email: string;
  numberOfUsers: number;
  expiredAt?: string;
}): Promise<PostLicense> => {
  const response = await axiosInstance.post(
    `${getBaseUrl()}/license`,
    licenseData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const patchLicense = async (
  id: number,
  licenseData: {
    name: string;
    email: string;
    numberOfUsers: number;
    expiredAt?: string | null;
  }
): Promise<PatchLicense> => {
  const response = await axiosInstance.patch(
    `${getBaseUrl()}/license/${id}`,
    licenseData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const deleteLicense = async (id: number): Promise<void> => {
  await axiosInstance.delete(`${getBaseUrl()}/license/${id}`, getHeaders());
};

// Link-related Functions
export const fetchLinkForPanoramaCreate = async (
  id: string
): Promise<LinksResponse> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/link/${id}`,
    getHeaders()
  );
  return response.data;
};

export const fetchLinkForPanoramaEdit = async (
  packageId: number,
  packageContentId: number = 0
): Promise<LinksResponse> => {
  const response = await axiosInstance.get(
    `${getBaseUrl()}/link/${packageId}?&packageContentId=${packageContentId}`,
    getHeaders()
  );
  return response.data;
};

export const postLink = async (linkData: LinkDataPost): Promise<void> => {
  const response = await axiosInstance.post(`${getBaseUrl()}/link`, linkData, {
    ...getHeaders(),
    headers: {
      ...getHeaders().headers,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const patchLink = async (
  id: number,
  locale: string,
  linkData: { targetPackageContentId: number; settings: string }
): Promise<void> => {
  const response = await axiosInstance.patch(
    `${getBaseUrl()}/link/${id}?locale=${locale}`,
    linkData,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

//ZIP related functions

export const uploadZipFile = async (packagesZip: File): Promise<void> => {
  const formData = new FormData();
  formData.append("packagesZip", packagesZip);

  await axiosInstance.post(`${getBaseUrl()}/package/zip/upload`, formData, {
    ...getHeaders(),
    headers: {
      ...getHeaders().headers,
      "Content-Type": "multipart/form-data",
    },
  });
};
